/**
 * hoy.ts — qué día es, en la zona del NEGOCIO.
 *
 * Es el gemelo en el frente de `control.hoy()` en la base (migración 0001), y
 * existe por el mismo motivo con otro reloj: el navegador de quien mira no
 * tiene por qué estar en Costa Rica, y `toISOString()` está en UTC.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ LO QUE ROMPÍA, Y NO ERA DECORATIVO
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `new Date().toISOString().slice(0, 10)` era la fecha por omisión de un
 * **asiento nuevo**. A partir de las 18:00 de Costa Rica, UTC ya es el día
 * siguiente: el formulario proponía **mañana**.
 *
 * Y un asiento fechado mañana no lo atrapa nada. La fecha es válida, el
 * periodo está abierto, el asiento cuadra. Entra en los libros y se queda.
 * Seis horas de cada día, todos los días.
 *
 * Las otras cuatro pantallas que lo hacían fallaban más despacio y por lo
 * mismo:
 *
 *   · el tablero y los reportes se pasaban al mes siguiente la noche del
 *     último día del mes;
 *   · la apertura proponía `<año>-01-01` con el año de UTC — un 31 de
 *     diciembre a las 18:01 ofrecía el 1 de enero del año EQUIVOCADO, que es
 *     el mismo defecto que la 0001 arregló del lado de la base.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ▶ Y NO SE USA LA HORA LOCAL DEL NAVEGADOR
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Sería el arreglo obvio y está mal por otro lado: el contador puede estar de
 * viaje. La fecha contable no depende de dónde esté la persona, sino de dónde
 * está el negocio. Se pide la fecha en `America/Costa_Rica`, siempre, y por
 * eso `ZONA` es una constante y no `undefined`.
 *
 * El día que entre una empresa fuera de Costa Rica, esto recibe la zona por
 * parámetro y sale de una columna de `control.empresas` — igual que su gemela
 * en la base. Hasta entonces suponerla sería inventar un dato que nadie
 * declaró.
 */

/** La zona del negocio. Una constante, no la del navegador. */
const ZONA = 'America/Costa_Rica';

/**
 * `en-CA` da exactamente `YYYY-MM-DD`, que es la forma que la base espera y la
 * que el resto del producto trata como fecha contable. No es una preferencia
 * de idioma: es el único formato de `Intl` que sale ya ordenado.
 */
const FORMATO = new Intl.DateTimeFormat('en-CA', {
  timeZone: ZONA,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Hoy en la zona del negocio, como `YYYY-MM-DD`. */
export function hoy(): string {
  return FORMATO.format(new Date());   // regla-ok: la única casa de `new Date()`
}

/** El año y el mes de hoy, ya en números, para las pantallas que eligen periodo. */
/**
 * El día de quien mira, en ISO y sin hora.
 *
 * ▶ `toISOString()` daría el de UTC, y en Costa Rica —UTC−6— eso es el día
 * siguiente durante las últimas seis horas: una factura tecleada a las 7 de la
 * noche nacería con fecha de mañana.
 *
 * ▶ Vive acá desde la `0118`. Estaba local en `Documentos.tsx`, y cuando Bancos
 * la necesitó la opción era copiarla — dos verdades sobre qué día es hoy.
 */
export function hoyISO(): string {
  const d = new Date();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mes}-${dia}`;
}

export function anioYMesDeHoy(): { anio: number; mes: number } {
  const [anio, mes] = hoy().split('-');
  return { anio: Number(anio), mes: Number(mes) };
}

/**
 * Cuántos días faltan para que termine el mes en curso.
 *
 * Se calcula sobre la fecha del NEGOCIO, no sobre la del navegador: si no, la
 * cuenta atrás del tablero se adelantaría un día seis horas cada tarde.
 */
export function diasQueQuedanDelMes(): number {
  const { anio, mes } = anioYMesDeHoy();
  const ultimo = new Date(Date.UTC(anio, mes, 0)).getUTCDate();
  const dia = Number(hoy().slice(8, 10));
  return ultimo - dia;
}

/**
 * La fecha a la que se fecha un estado financiero del periodo `anio-mes`.
 *
 * ▶ POR QUÉ NO ES «EL ÚLTIMO DÍA DEL MES» A SECAS.
 *
 * Eso era, y el 5 de setiembre de 2026 el Balance General en producción salía
 * rotulado «al 30 sept 2026». Los saldos eran los del día 5 —ciertos—; la
 * fecha los ponía veinticinco días adelante. En un balance la fecha no
 * acompaña al documento: **es** el documento. Los mismos números al 5 y al 30
 * son dos afirmaciones distintas, y la impresa era la que no se podía
 * sostener.
 *
 * Un mes cerrado no se toca: agosto se fecha al 31 de agosto. El tope solo
 * muerde en el mes en curso —y en cualquier mes futuro, que el selector de
 * periodo ofrece los doce del año.
 *
 * `hoyISO` entra por parámetro para poder probarlo sin mover el reloj.
 */
export function finDelEstado(anio: number, mes: number, hoyISO: string = hoy()): string {
  const ultimo = `${anio}-${String(mes).padStart(2, '0')}-`
    + String(new Date(Date.UTC(anio, mes, 0)).getUTCDate()).padStart(2, '0');
  // Se comparan como texto: dos ISO del mismo largo ordenan igual que sus
  // fechas, y pasarlas por `Date` para compararlas es justo el paso que corre
  // el día en las zonas negativas.
  return ultimo > hoyISO ? hoyISO : ultimo;
}
